import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Download, IndianRupee, MessageSquare, Receipt, Settings2, Zap, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

interface QuickAction {
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  /** A route, or a button when the action opens a dialog on this screen. */
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface FeeQuickActionsProps {
  canCollect: boolean;
  canRemind: boolean;
  canView: boolean;
  canManage: boolean;
  /** Opens the counter on a student the office picks. */
  onCollectFee: () => void;
  onRemindEverybody: () => void;
  /** Saves the register as it is currently filtered. */
  onDownloadReport: () => void;
  isDownloading: boolean;
  /** Switches the page to the day book — every receipt taken. */
  onOpenDayBook: () => void;
  /**
   * Lists what each class is charged. Absent at a college, which has no class
   * fees to list, so the action goes to the Classes page instead.
   */
  onOpenFeeSettings?: () => void;
}

export function FeeQuickActions({
  canCollect,
  canRemind,
  canView,
  canManage,
  onCollectFee,
  onRemindEverybody,
  onDownloadReport,
  isDownloading,
  onOpenDayBook,
  onOpenFeeSettings,
}: FeeQuickActionsProps) {
  const actions: QuickAction[] = [
    ...(canCollect
      ? [
          {
            key: 'collect',
            label: 'Receive fee',
            description: 'Record a new fee payment',
            icon: IndianRupee,
            iconBg: 'bg-emerald-50 dark:bg-emerald-950/80',
            iconColor: 'text-emerald-600 dark:text-emerald-400',
            onClick: onCollectFee,
          },
        ]
      : []),
    ...(canRemind
      ? [
          {
            key: 'remind',
            label: 'Send fee reminder',
            description: 'One message to every family with a balance',
            icon: MessageSquare,
            iconBg: 'bg-indigo-50 dark:bg-indigo-950/80',
            iconColor: 'text-indigo-600 dark:text-indigo-400',
            onClick: onRemindEverybody,
          },
        ]
      : []),
    ...(canView
      ? [
          {
            key: 'report',
            label: 'Download fee report',
            description: isDownloading
              ? 'Collecting the register…'
              : 'The register as a spreadsheet',
            icon: Download,
            iconBg: 'bg-sky-50 dark:bg-sky-950/80',
            iconColor: 'text-sky-600 dark:text-sky-400',
            onClick: onDownloadReport,
            disabled: isDownloading,
          },
          {
            key: 'daybook',
            label: 'Collections day book',
            description: 'Every receipt issued, and what was cancelled',
            icon: Receipt,
            iconBg: 'bg-amber-50 dark:bg-amber-950/80',
            iconColor: 'text-amber-600 dark:text-amber-400',
            onClick: onOpenDayBook,
          },
        ]
      : []),
    ...(canManage
      ? [
          {
            key: 'settings',
            label: 'Fee settings',
            description: 'What each class is charged',
            icon: Settings2,
            iconBg: 'bg-purple-50 dark:bg-purple-950/80',
            iconColor: 'text-purple-600 dark:text-purple-400',
            ...(onOpenFeeSettings ? { onClick: onOpenFeeSettings } : { to: '/admin/classes' }),
          },
        ]
      : []),
  ];

  if (actions.length === 0) return null;

  return (
    <section className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-5 shadow-xs">
      <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 dark:border-slate-800">
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-purple-50 dark:bg-purple-950/80 text-purple-600 dark:text-purple-400 font-bold"
          aria-hidden
        >
          <Zap className="h-4 w-4" />
        </span>
        <h2 className="text-base font-extrabold text-slate-900 dark:text-white tracking-tight">Quick actions</h2>
      </div>

      <div className="mt-2.5 flex flex-col gap-1">
        {actions.map((action) => {
          const Icon = action.icon;

          const body: ReactNode = (
            <>
              <span
                className={cn(
                  'grid h-8 w-8 shrink-0 place-items-center rounded-xl font-bold',
                  action.iconBg,
                  action.iconColor,
                )}
                aria-hidden
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-slate-800 dark:text-slate-100">{action.label}</span>
                <span className="mt-0.5 block truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                  {action.description}
                </span>
              </span>
            </>
          );

          const shell = cn(
            'flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors',
            'hover:bg-slate-50 dark:hover:bg-slate-800/60 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 select-none active:scale-[0.99]',
            action.disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
          );

          return action.to ? (
            <Link key={action.key} to={action.to} className={shell}>
              {body}
            </Link>
          ) : (
            <button
              key={action.key}
              type="button"
              disabled={action.disabled}
              onClick={action.onClick}
              className={shell}
            >
              {body}
            </button>
          );
        })}
      </div>
    </section>
  );
}
